<script setup lang="ts">
import { CV_COURSES_NOTE, type CvDocument } from '../stores/cv_versions'

// Read-only rendering of one structured CV document, section by section,
// drawn as a light serif page so the admin reviews something that reads
// like the CV. It is a review view, not the public layout; the server
// renderer owns how the public page looks to a recruiter. The optional
// name / contact line come from the profile, which the document omits.
defineProps<{ document: CvDocument; name?: string | null; contact?: string }>()

function period(start: string, end: string): string {
  if (!start && !end) return ''
  return `${start || '?'} – ${end || '?'}`
}
</script>

<template>
  <article
    class="cv-paper flex flex-col gap-[18px] rounded-xl bg-(--cv-paper) px-5 py-8 font-serif text-base leading-[1.55] text-(--cv-ink) sm:px-12 sm:py-11"
    data-testid="cv-document"
  >
    <header v-if="name || contact">
      <div v-if="name" class="text-[28px] leading-tight font-medium tracking-tight sm:text-[34px]">{{ name }}</div>
      <div v-if="contact" class="mt-1 font-sans text-sm text-(--cv-meta)">{{ contact }}</div>
    </header>
    <section v-if="document.summary" class="cv-section" data-testid="cv-summary">
      <h3 class="cv-heading mb-1.5">Summary</h3>
      <p class="m-0 text-[17px] leading-relaxed">{{ document.summary }}</p>
    </section>

    <section v-if="document.approach.length" class="cv-section" data-testid="cv-approach">
      <h3 class="cv-heading mb-2">Leadership &amp; approach</h3>
      <ul class="m-0 pl-4 flex flex-col gap-1">
        <li v-for="(point, i) in document.approach" :key="i" :data-testid="`cv-approach-${i}`">
          <span class="font-medium">{{ point.heading }}</span> — {{ point.detail }}
        </li>
      </ul>
    </section>

    <section v-if="document.roles.length" class="cv-section" data-testid="cv-roles">
      <h3 class="cv-heading mb-2">Experience</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(role, i) in document.roles" :key="i" :data-testid="`cv-role-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-lg font-medium">{{ role.title }}</span>
            <span class="font-sans text-[13px] text-(--cv-meta)">{{ period(role.start, role.end) }}</span>
          </div>
          <div class="text-(--cv-meta)">
            {{ role.employer }}<span v-if="role.location"> · {{ role.location }}</span>
          </div>
          <ul v-if="role.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in role.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.board_roles.length" class="cv-section" data-testid="cv-board-roles">
      <h3 class="cv-heading mb-2">Board positions</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(board, i) in document.board_roles" :key="i" :data-testid="`cv-board-role-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-lg font-medium">{{ board.title }}</span>
            <span class="font-sans text-[13px] text-(--cv-meta)">{{ period(board.start, board.end) }}</span>
          </div>
          <div class="text-(--cv-meta)">
            {{ board.organisation }}<span v-if="board.description"> · {{ board.description }}</span>
          </div>
          <ul v-if="board.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in board.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.projects.length" class="cv-section" data-testid="cv-projects">
      <h3 class="cv-heading mb-2">Projects</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(project, i) in document.projects" :key="i" :data-testid="`cv-project-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <a
              v-if="project.url"
              :href="project.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-lg font-medium text-(--cv-ink) underline decoration-(--cv-rule)"
              >{{ project.name }}</a
            >
            <span v-else class="text-lg font-medium">{{ project.name }}</span>
            <span class="font-sans text-[13px] text-(--cv-meta)">{{ project.period }}</span>
          </div>
          <div class="text-(--cv-meta)">{{ project.description }}</div>
          <ul v-if="project.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in project.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.education.length" class="cv-section" data-testid="cv-education">
      <h3 class="cv-heading mb-2">Education</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-2">
        <li v-for="(edu, i) in document.education" :key="i">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-lg font-medium">{{ edu.degree }}</span>
            <span class="font-sans text-[13px] text-(--cv-meta)">{{ period(edu.start, edu.end) }}</span>
          </div>
          <div class="text-(--cv-meta)">{{ edu.institution }}</div>
          <ul v-if="edu.details.length" class="m-0 mt-1 pl-4">
            <li v-for="(detail, j) in edu.details" :key="j">{{ detail }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.skills.length" class="cv-section" data-testid="cv-skills">
      <h3 class="cv-heading mb-2">Skills</h3>
      <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <template v-for="(group, i) in document.skills" :key="i">
          <dt class="text-(--cv-meta)">{{ group.category }}</dt>
          <dd class="m-0">{{ group.items.join(', ') }}</dd>
        </template>
      </dl>
    </section>

    <section v-if="document.languages.length" class="cv-section" data-testid="cv-languages">
      <h3 class="cv-heading mb-1.5">Languages</h3>
      <p class="m-0">
        <span v-for="(lang, i) in document.languages" :key="i">
          <span v-if="i > 0"> · </span>{{ lang.language
          }}<span v-if="lang.level" class="text-(--cv-meta)"> ({{ lang.level }})</span>
        </span>
      </p>
    </section>

    <p
      class="m-0 border-t border-(--cv-rule) pt-3.5 font-sans text-[13px] text-(--cv-meta)"
      data-testid="cv-courses-note"
    >
      {{ CV_COURSES_NOTE }}
    </p>
  </article>
</template>

<style scoped>
/* The paper's own palette: a light page inside the dark app, the one
   place the SPA renders dark-on-light. Values match the Live CV design. */
.cv-paper {
  --cv-paper: #f6f4ee;
  --cv-ink: #1b1c1e;
  --cv-meta: #4a4d52;
  --cv-rule: #d9d5cb;
}

.cv-paper :where(a):focus-visible {
  outline-color: var(--cv-ink);
}

.cv-section {
  border-top: 1px solid var(--cv-rule);
  padding-top: 14px;
}

.cv-heading {
  margin-top: 0;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cv-meta);
}
</style>
